import { getFullDate } from '@/helpers/date.helpers'
import { toast } from '@/helpers/toast/toast.helper'
import { ChatModel } from '@/models/chat.model'
import { SelectedDepartmentModel } from '@/models/department.model'
import { MessageModel } from '@/models/message.model'
import { CreateMessageRequestParam } from '@/redux/message/actions'
import { sendMessageToZApiMultiService } from '@/redux/message/sagas/createMessageSaga'
import { buildUnOfficialMessage } from '@/redux/message/sagas/createMessageSaga/helpers'
import { buildFilesData, sendMessageOfficialAPI } from '@/redux/message/sagas/createMessageSaga/http/message'
import {
  SendMessageOfficialAPIDTO,
  SendMessageOfficialAPIResponse,
  SendMessageResponse
} from '@/redux/message/sagas/createMessageSaga/http/message/types'
import { generateUniqueTicketNumber } from '@/redux/message/utils'
import { t } from 'i18next'
import { call, put } from 'redux-saga/effects'
import { v4 as uuid } from 'uuid'
import { createNewChatAction, setActiveChatAction } from '../../handles'
import { create, getChatsByStatus } from '../_http/chat'
import { saveMany } from '../_http/message'
import { buildOfficialMessage } from './helpers'

export function* createNewChatSaga(action: ReturnType<typeof createNewChatAction.request>) {
  const { message, phoneNumberId, ownerId, token, campaignId, callBack, department } = action.payload
  try {
    const newMessage: MessageModel = yield buildNewMessage(message)

    const { chats } = yield getChatsByStatus({
      chatFilters: { requesterNumber: newMessage.senderNumber.toString() },
      campaignIds: [campaignId],
      ownerId,
      status: ['open', 'in_progress'],
      queryLimit: 1
    })

    if (!chats?.length) {
      let newChat: ChatModel = {} as ChatModel
      if (newMessage?.isOfficial) {
        const sendMessageOfficialAPIResponse: SendMessageOfficialAPIResponse = yield sendMessageOfficialAPI({
          campaignId,
          messageId: newMessage?.templateMessageId,
          phone: newMessage.phone,
          typeSent: newMessage.messageTypeSent,
          phoneNumberId,
          text: newMessage.messageText,
          messageType: newMessage.typeFile,
          caption: newMessage.messageText || '',
          filesData: buildFilesData(newMessage.files, false)
        } as SendMessageOfficialAPIDTO)

        const sendedMessages = buildOfficialMessage(newMessage, sendMessageOfficialAPIResponse)

        newChat = yield call(buildNewChat, { message: sendedMessages[sendedMessages.length - 1], ownerId, department })

        const sendedMessagesInCludedChatId = sendedMessages.map(currentSendedMessage => ({
          ...currentSendedMessage,
          chatId: newChat.id,
          receiverNumber: currentSendedMessage.senderNumber.toString(),
          senderNumber: currentSendedMessage.receiverNumber.toString()
        }))
        yield saveMany(sendedMessagesInCludedChatId)
      } else {
        const sendMessageResult: SendMessageResponse = yield call(
          sendMessageToZApiMultiService,
          newMessage,
          campaignId,
          token
        )
        const sendedMessages = buildUnOfficialMessage(newMessage, sendMessageResult)

        newChat = yield call(buildNewChat, { message: sendedMessages[sendedMessages.length - 1], ownerId, department })

        const sendedMessagesInCludedChatId = sendedMessages.map(currentSendedMessage => ({
          ...currentSendedMessage,
          chatId: newChat.id,
          receiverNumber: currentSendedMessage.senderNumber.toString(),
          senderNumber: currentSendedMessage.receiverNumber.toString()
        }))
        yield saveMany(sendedMessagesInCludedChatId)
      }

      const createdChat: ChatModel = yield create(newChat)

      yield put(setActiveChatAction.request({ activeChat: { ...(createdChat || newChat), unreadMessagesCount: 0 } }))
      yield put(createNewChatAction.success())
      callBack()
    } else {
      const chat = chats[0]
      const errorMessage = `${t('translation:createNewChatSagaError.chatAlreadyExists')} ${chat.department.name}`
      toast({
        type: 'error',
        message: errorMessage
      })
      yield put(createNewChatAction.failure(new Error(errorMessage)))
    }
  } catch (error) {
    toast({ type: 'error', message: t('translation:createNewChatSagaError.createChatError') })
    console.log({ createNewChatSaga: error })
    yield put(createNewChatAction.failure(error))
  }
}

async function buildNewMessage(message: CreateMessageRequestParam['message']) {
  return {
    ...message,
    messageId: '',
    isRead: false,
    isSend: false,
    fromMe: true,
    createdAt: new Date().getTime()
  }
}

type BuildNewChat = {
  message: MessageModel
  ownerId: string
  department: SelectedDepartmentModel
}

async function buildNewChat({ message, ownerId, department }: BuildNewChat): Promise<Partial<ChatModel>> {
  const baseDate = new Date()
  const dateNow = baseDate.getTime()
  const dateMonth = baseDate.getMonth()
  return {
    id: uuid(),
    department: { id: department?.id, name: department?.name, color: department?.color },
    departmentId: department?.id,
    ownerId,
    campaignId: message.campanhaId,
    status: 'in_progress',
    unreadMessagesCount: 0,
    receiverNumber: message.receiverNumber.toString(),
    requesterNumber: message.senderNumber.toString(),
    chatName: message.senderName || message.senderNumber.toString(),
    operatorsId: [message.operator.id],
    operators: [message.operator],
    startDate: dateNow,
    endDate: null,
    month: dateMonth,
    year: baseDate.getFullYear(),
    fullDate: getFullDate(baseDate),
    isFinishedUra: false,
    duration: 0,
    ticket: generateUniqueTicketNumber(),
    lastMessage: message as any,
    updatedAt: dateNow,
    createdAt: dateNow,
    durations: [{ operatorId: message.operator.id, periods: [{ startTime: Date.now() }] }]
  }
}
